import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Lock,
  ShoppingCart as ShoppingCartIcon,
  Minus,
  Plus,
  ArrowRight,
  ArrowLeft,
  ShoppingBag,
  RotateCcw,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { useLanguage } from "../../../contexts/LanguageContext.js";
import api from "../../../api/client.js";
import { t } from "../../../i18n.js";
import logger from "../../../utils/logger.js";
import LoadingScreen from "../../../components/ui/Loading/Loading.js";
import Breadcrumb from "../../../components/ui/Breadcrumb/Breadcrumb.js";
import "./ShoppingCart.css";

export default function ShoppingCart() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needLogin, setNeedLogin] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [hasPendingDeletes, setHasPendingDeletes] = useState(false);

  // Undo state
  const undoTimers = useRef(new Map());
  // Items pending server delete (undo window not yet expired)
  const pendingDeletes = useRef(new Map());
  // Prevents concurrent loadCart calls (StrictMode / lang change)
  const isLoadingCart = useRef(false);

  const toImageUrl = (raw) => {
    const v = String(raw || "").trim();
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v;
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";
    if (/^\/?uploads\//i.test(v)) {
      return `${origin}/${v.replace(/^\/+/, "")}`;
    }
    return `${origin}/uploads/products/${v.replace(/^\/+/, "")}`;
  };

  const getItemLineTotal = (i) => {
    if (i.line_total !== undefined && i.line_total !== null) {
      return Number(i.line_total);
    }
    return Number(i.unit_price || i.price || 0) * Number(i.quantity || 0);
  };

  const getItemName = (i) => {
    if (isAr) {
      return (
        i?.variant?.product?.name_ar ||
        i?.product?.name_ar ||
        i?.name_ar ||
        i?.variant?.product?.name_en ||
        i?.product?.name_en ||
        i?.name_en ||
        i?.name ||
        "منتج"
      );
    }
    return (
      i?.variant?.product?.name_en ||
      i?.product?.name_en ||
      i?.name_en ||
      i?.variant?.product?.name_ar ||
      i?.product?.name_ar ||
      i?.name_ar ||
      i?.name ||
      "Product"
    );
  };

  const getItemImage = (i) => {
    return (
      i?.variant?.image_url ||
      i?.variant?.product?.image_url ||
      i?.product?.image_url ||
      toImageUrl(
        i?.variant?.image ||
          i?.variant?.product?.image ||
          i?.product?.image ||
          i?.image
      )
    );
  };

  const total = items.reduce((sum, i) => sum + getItemLineTotal(i), 0);
  const itemCount = items.reduce((sum, i) => sum + Number(i.quantity || 0), 0);

  const syncCartToStorage = (cartItems) => {
    localStorage.setItem("cart", JSON.stringify(cartItems));
    window.dispatchEvent(new Event("cart-updated"));
  };

  async function loadCart() {
    // Guard: prevents double-call from React 18 StrictMode or lang-change re-render
    if (isLoadingCart.current) return;
    isLoadingCart.current = true;

    try {
      setLoading(true);
      setNeedLogin(false);

      const res = await api.get("cart");
      const cartItems = Array.isArray(res?.items) ? res.items : [];
      setItems(cartItems);
      syncCartToStorage(cartItems);
    } catch (e) {
      logger.error("Error loading cart", e);
      if (e?.status === 401) {
        setNeedLogin(true);
        setItems([]);
        localStorage.removeItem("cart");
        window.dispatchEvent(new Event("cart-updated"));
      } else {
        toast.error(t(lang, "server_error"), {
          toastId: "cart-load-error",
          position: isAr ? "top-left" : "top-right",
          rtl: isAr,
        });
      }
    } finally {
      setLoading(false);
      isLoadingCart.current = false;
    }
  }

  useEffect(() => {
    loadCart();
    return () => {
      // On unmount: cancel timers and flush any pending server deletes
      undoTimers.current.forEach((timer) => clearTimeout(timer));
      undoTimers.current.clear();
      pendingDeletes.current.forEach(({ removed }) => {
        performServerDelete(removed);
      });
      pendingDeletes.current.clear();
    };
  }, []);

  async function updateQty(id, qty) {
    const quantity = Math.max(1, Number(qty) || 1);
    const prevItems = [...items];

    const optimisticItems = items.map((item) =>
      item.id === id ? { ...item, quantity } : item
    );
    setItems(optimisticItems);
    syncCartToStorage(optimisticItems);
    setUpdatingId(id);

    try {
      await api.post(`cart/${id}`, { quantity, _method: "PUT" });
      const res = await api.get("cart");
      const cartItems = Array.isArray(res?.items) ? res.items : [];
      setItems(cartItems);
      syncCartToStorage(cartItems);
    } catch (e) {
      logger.error("Error updating quantity", e);
      setItems(prevItems);
      syncCartToStorage(prevItems);
      toast.error(isAr ? "تعذر تحديث الكمية" : "Error updating quantity", {
        position: isAr ? "top-left" : "top-right",
        rtl: isAr,
      });
    } finally {
      setUpdatingId(null);
    }
  }

  // ── Smart Remove with Undo ──
  async function handleRemove(id) {
    const index = items.findIndex((i) => i.id === id);
    if (index === -1) return;

    const removed = items[index];
    const prevItems = [...items];

    // 1. Remove immediately from UI 
    const newItems = items.filter((i) => i.id !== id);
    setItems(newItems);
    syncCartToStorage(newItems);

    // 2. Register as pending delete — store item + full snapshot for undo
    pendingDeletes.current.set(id, { removed, prevItems });
    setHasPendingDeletes(true);

    // Clear any existing pending timer for this item
    if (undoTimers.current.has(id)) {
      clearTimeout(undoTimers.current.get(id));
      undoTimers.current.delete(id);
    }

    // 3. Show toast with UNDO
    const itemName = getItemName(removed);
    const toastId = toast.success(
      <div className="cart-undo-toast">
        <span className="cart-undo-toast__text">
          {isAr ? `تمت إزالة "${itemName}"` : `"${itemName}" removed`}
        </span>
        <button
          type="button"
          className="cart-undo-toast__btn"
          onClick={() => {
            if (undoTimers.current.has(id)) {
              clearTimeout(undoTimers.current.get(id));
              undoTimers.current.delete(id);
            }

            const pending = pendingDeletes.current.get(id);
            pendingDeletes.current.delete(id);
            toast.dismiss(toastId);

            if (!pending) return;
            setItems(pending.prevItems);
            syncCartToStorage(pending.prevItems);
            setHasPendingDeletes(pendingDeletes.current.size > 0);
           performServerRestore(pending.removed, setItems);

            toast.info(
              isAr
                ? `تمت استعادة "${itemName}"`
                : `"${itemName}" restored`,
              { position: isAr ? "top-left" : "top-right", rtl: isAr, autoClose: 2000 }
            );
          }}
        >
          <RotateCcw size={14} />
          {isAr ? "تراجع" : "Undo"}
        </button>
      </div>,
      {
        position: isAr ? "top-left" : "top-right",
        rtl: isAr,
        autoClose: 5000,
        closeOnClick: false,
        draggable: false,
      }
    );
    const timer = setTimeout(() => {
      undoTimers.current.delete(id);
      pendingDeletes.current.delete(id);
      setHasPendingDeletes(pendingDeletes.current.size > 0);
      toast.dismiss(toastId);
      performServerDelete(removed);
    }, 5000);

    undoTimers.current.set(id, timer);
  }
  async function performServerRestore(removed, updater) {
    try {
      await api.post(`cart/${removed.id}`, {
        quantity: removed?.quantity || 1,
        _method: "PUT",
      });
      const res = await api.get("cart");
      const cartItems = Array.isArray(res?.items) ? res.items : [];
      syncCartToStorage(cartItems);
      setItems(cartItems);
    } catch (e) {
      logger.error("Error restoring item", e);
    }
  }

  async function performServerDelete(removed) {
    try {
      if (typeof api.delete === "function") {
        await api.delete(`cart/${removed.id}`);
      } else {
        await api.post(`cart/${removed.id}`, { _method: "DELETE" });
      }
    } catch (e) {
      logger.error("Error removing item", e);
      toast.error(t(lang, "server_error"), {
        position: isAr ? "top-left" : "top-right",
        rtl: isAr,
      });
    }
  }

  const breadcrumbItems = [
    { label: isAr ? "المتجر" : "Shop", link: "/shop" },
    { label: t(lang, "cart_title"), link: "/cart" },
  ];

  const getSubtitle = () => {
    if (isAr) {
      if (itemCount === 0) return "لا توجد منتجات في السلة";
      if (itemCount === 1) return "منتج واحد في السلة";
      if (itemCount === 2) return "منتجان في السلة";
      if (itemCount >= 3 && itemCount <= 10) return `${itemCount} منتجات في السلة`;
      return `${itemCount} منتج في السلة`;
    }
    if (itemCount === 0) return "Your cart is empty";
    if (itemCount === 1) return "1 item in your cart";
    return `${itemCount} items in your cart`;
  };

  if (loading) {
    return (
      <div className="cart-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="cart-container">
          <LoadingScreen lang={lang} />
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page" dir={isAr ? "rtl" : "ltr"}>
      <Breadcrumb items={breadcrumbItems} className="breadcrumb-glass" />

      {/* Header */}
      <header className="cart-header">
        <div className="cart-header-content">
          <div className="cart-title-wrapper">
            <div className="cart-header-icon">
              <ShoppingCartIcon size={24} strokeWidth={2} />
            </div>
            <div className="cart-title-group">
              <h1 className="cart-header-title">{t(lang, "cart_title")}</h1>
              {!needLogin && (
                <p className="cart-header-subtitle">{getSubtitle()}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cart-container">
        {needLogin ? (
          <div className="cart-login-state">
            <div className="cart-login-state__icon-wrap">
              <Lock size={48} strokeWidth={1.2} />
            </div>
            <h2 className="cart-login-state__title">
              {isAr ? "تسجيل الدخول مطلوب" : "Sign in required"}
            </h2>
            <p className="cart-login-state__text">
              {isAr
                ? "سجل دخولك للوصول إلى سلة المشتريات وإتمام طلباتك."
                : "Sign in to access your cart and complete your orders."}
            </p>
            <Link to="/login" className="btn btn-primary btn-glow rounded-pill px-5 py-2">
              <Lock size={18} style={{ marginInlineEnd: 8 }} />
              {isAr ? "تسجيل الدخول" : "Sign In"}
            </Link>
          </div>
        ) : items.length === 0 ? (
          <div className="cart-empty-state">
            <div className="cart-empty-state__icon-wrap">
              <ShoppingCartIcon size={48} strokeWidth={1} />
            </div>
            <h2 className="cart-empty-state__title">{t(lang, "cart_empty")}</h2>
            <p className="cart-empty-state__text">
              {isAr
                ? "استكشف منتجاتنا وابدأ بتجميع سلة مشترياتك الآن."
                : "Explore our products and start building your cart today."}
            </p>
            <Link to="/shop" className="cart-btn-browse">
              <ShoppingBag size={18} />
              {isAr ? "تصفح المتجر" : "Browse Shop"}
            </Link>
          </div>
        ) : (
          <div className="cart-layout">
            {/* Summary */}
            <aside className="cart-summary-col">
              <div className="cart-summary">
                <h5 className="cart-summary__title">
                  {isAr ? "ملخص الطلب" : "Order Summary"}
                </h5>

                <div className="cart-summary__row">
                  <span>{isAr ? "عدد المنتجات" : "Items"}</span>
                  <span className="cart-summary__value">{itemCount}</span>
                </div>

                <div className="cart-summary__row cart-summary__row--total">
                  <span>{isAr ? "الإجمالي" : "Total"}</span>
                  <span className="cart-summary__total-value">
                    ${total.toFixed(2)}
                  </span>
                </div>

                <hr className="cart-summary__divider" />

                <Link
                  to={hasPendingDeletes ? "#" : "/checkout"}
                  className={`cart-btn cart-btn--primary${hasPendingDeletes ? " cart-btn--pending" : ""}`}
                  aria-disabled={hasPendingDeletes}
                  onClick={(e) => { if (hasPendingDeletes) e.preventDefault(); }}
                >
                  <span className="cart-btn__inner">
                    {hasPendingDeletes
                      ? (isAr ? "انتظر…" : "Please wait…")
                      : (isAr ? "إتمام الدفع" : "Checkout")
                    }
                    {!hasPendingDeletes && (isAr ? <ArrowLeft size={16} /> : <ArrowRight size={16} />)}
                  </span>
                </Link>

                <Link to="/shop" className="cart-btn cart-btn--secondary">
                  <span className="cart-btn__inner">
                    {isAr ? "متابعة التسوق" : "Continue Shopping"}
                  </span>
                </Link>
              </div>
            </aside>

            {/* Cart Items */}
            <div className="cart-items-col">
              <div className="cart-items-panel">
                {items.map((i, index) => {
                  const imageSrc = getItemImage(i);
                  const itemName = getItemName(i);
                  const qty = Math.max(1, Number(i.quantity || 1));
                  const unitPrice = Number(i.unit_price || i.price || 0);
                  const lineTotal = getItemLineTotal(i);
                  const isUpdating = updatingId === i.id;

                  return (
                    <div
                      key={i.id}
                      className={`cart-item ${index !== items.length - 1 ? "cart-item--bordered" : ""} ${isUpdating ? "cart-item--updating" : ""}`}
                    >
                      <div className="cart-item__image-wrap">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={itemName}
                            className="cart-item__image"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const fallback = e.currentTarget.parentElement?.querySelector(".cart-item__fallback");
                              if (fallback) fallback.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className="cart-item__fallback"
                          style={{ display: imageSrc ? "none" : "flex" }}
                        >
                          <ShoppingCartIcon size={24} strokeWidth={1.5} />
                        </div>
                      </div>

                      <div className="cart-item__body">
                        <div className="cart-item__main">
                          <div className="cart-item__info">
                            <h5 className="cart-item__name">{itemName}</h5>
                            {i?.variant?.sku && (
                              <div className="cart-item__sku">
                                SKU: {i.variant.sku}
                              </div>
                            )}
                            <div className="cart-item__unit-price">
                              {isAr ? "سعر الوحدة:" : "Unit price:"}{" "}
                              <span className="cart-item__price-value">
                                ${unitPrice.toFixed(2)}
                              </span>
                            </div>
                          </div>

                          <div className="cart-item__total">
                            <div className="cart-item__total-label">
                              {isAr ? "الإجمالي" : "Total"}
                            </div>
                            <div className="cart-item__total-value">
                              ${lineTotal.toFixed(2)}
                            </div>
                          </div>
                        </div>

                        <div className="cart-item__actions">
                          <div className={`qty-control ${isUpdating ? "qty-control--updating" : ""}`}>
                            <button
                              type="button"
                              className="qty-control__btn"
                              onClick={() => updateQty(i.id, qty - 1)}
                              disabled={qty <= 1 || isUpdating}
                              aria-label={isAr ? "تقليل الكمية" : "Decrease quantity"}
                            >
                              <Minus size={16} strokeWidth={2.5} />
                            </button>
                            <span className="qty-control__value">{qty}</span>
                            <button
                              type="button"
                              className="qty-control__btn"
                              onClick={() => updateQty(i.id, qty + 1)}
                              disabled={isUpdating}
                              aria-label={isAr ? "زيادة الكمية" : "Increase quantity"}
                            >
                              <Plus size={16} strokeWidth={2.5} />
                            </button>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleRemove(i.id)}
                            className="btn-remove"
                          >
                            {isAr ? "إزالة" : "Remove"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}